import React, {useEffect} from 'react';
import './App.css';
import {useResources} from "./stores/ResourceProvider";
import { ThemeProvider } from '@mui/material/styles';
import theme from "./styles/theme";
import {Routes, Route, BrowserRouter} from "react-router-dom";
import ControlTower from "./components/navigation/ControlTower";
import RoutesConfig, {RoutesConfigHelpers} from "./RoutesConfig";
import CalcPage from "./pages/calc/CalcPage";
// Config
import config, { Options } from 'react-global-configuration';
import AppConfig from "./AppConfig";
import {Amplify} from "aws-amplify";
import amplifyconfig from './amplify_outputs.json';
// import amplifyconfig from './amplifyconfiguration.json';
import SignInPage from "./pages/SignInPage";
import SignOutPage from "./pages/SignOutPage";
import HomePage from "./pages/home/HomePage";
import SettingsPage from "./pages/settings/SettingsPage";
import AdminPage from "./pages/admin/AdminPage";
import SignupPage from "./pages/SignupPage";
import InvitePage from "./pages/InvitePage";
import CommunityPage from "./pages/Community";
import CheckoutReturnPage from "./pages/checkout/CheckoutReturnPage";
import ReviewPage from "./pages/review/ReviewPage";
import NotFoundPage from "./pages/NotFoundPage";

config.set(AppConfig, { freeze: false, assign: false } as Options);

const originalConfig = amplifyconfig as any
const customApiConfig = originalConfig.custom?.API ?? {};
const restEndpoints = Object.entries(customApiConfig).reduce(
  (acc, [name, cfg]: [string, any]) => {
    const key = name.replace(/-[^-]+$/, '');
    acc[key] = { endpoint: cfg.endpoint, region: cfg.region };
    return acc;
  },
  {} as Record<string, { endpoint: string; region: string }>
);
// First pass: let Amplify parse amplify_outputs.json normally (Auth, GraphQL, etc.)
Amplify.configure(originalConfig);
// Second pass: merge in REST endpoints from custom.API. This must be a plain
// ResourcesConfig (no `version` field) so Amplify doesn't re-run the
// amplify_outputs parser, which strips custom.API.
const parsedConfig = Amplify.getConfig();
Amplify.configure({
  ...parsedConfig,
  API: {
    ...(parsedConfig.API ?? {}),
    REST: restEndpoints,
  },
});

// A component for "warming up" the store objects, b/c we need to call these init functions wrapped within the RootStoreProvider component.
const AppStartup = ({
  children
}: {
  children: React.ReactNode
}) => {
  const stores = useResources()
  const location = window.location

  useEffect(() => {
    const initStores = async () => {
      // await stores.init()
      const { userStore, accountStore, modelStore } = stores
      if (userStore) {
        const pathname = `/${RoutesConfigHelpers.getPathSegment(location.pathname, 1)}`
        if (pathname !== RoutesConfig.signIn.pathname &&
            pathname !== RoutesConfig.signUp.pathname &&
            pathname !== RoutesConfig.invite.pathname) {

            const user = await userStore.reloadAuthenticatedUser()
            if (user) {
                accountStore.init(user.account)
                await modelStore.loadUserModels(user)
            }
        }
      }
    }
    initStores()
  }, [
    stores
  ])
  return (
    <React.Fragment>
      {children}
    </React.Fragment>
  )
}

function App() {
  const versionCheckInterval = 10 * 60000; // Check every 10 minutes
  const {notify, userStore} = useResources()

  const disableSignup = config.get("disableSignup")

  useEffect(() => {
    const checkVersion = async () => {
      if (!userStore.isAuthenticated || config.get("domain") === "localhost") {
        return
      }

      try {
        // console.log('Checking for new version...');
        const localVersion = config.get('version')
        // console.log('Local Version:', localVersion);
        const response = await fetch('/manifest.json');
        const data = await response.json();
        const remoteVersion = data['version']
        // console.log('Remote Version:', remoteVersion);

        if (localVersion && remoteVersion && remoteVersion !== localVersion) {
          // console.log(`New version available ${remoteVersion}`);
          notify.show("info",`A new version of RBC is available. Refresh the page to update.`)
        }

      } catch (error) {
        console.error('Failed to check for new version', error);
      }
    };

    checkVersion();
    const intervalId = setInterval(checkVersion, versionCheckInterval);

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [userStore.user]);

  return (
    <AppStartup>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<ControlTower routes={RoutesConfig} />}>
              <Route path={`${RoutesConfig.admin.pathname}/:view`} element={<AdminPage />} />
              <Route path={`${RoutesConfig.admin.pathname}`} element={<AdminPage />} />
              <Route path={RoutesConfig.home.pathname} element={<HomePage />} />
              <Route path={`${RoutesConfig.calc.pathname}/:view`} element={<CalcPage />} />
              <Route path={`${RoutesConfig.calc.pathname}/:view/:year`} element={<CalcPage />} />
              <Route path={RoutesConfig.calc.pathname} element={<CalcPage />} />
              <Route path={`${RoutesConfig.checkoutReturn.pathname}`} element={<CheckoutReturnPage/>} />
              <Route path={`${RoutesConfig.checkoutReturn.pathname}/:sessionId`} element={<CheckoutReturnPage/>} />
              <Route path={RoutesConfig.community.pathname} element={<CommunityPage />} />
              <Route path={`${RoutesConfig.invite.pathname}/:token`} element={<InvitePage />} />
              <Route path={`${RoutesConfig.review.pathname}/:view`} element={<ReviewPage />} />
              <Route path={`${RoutesConfig.review.pathname}/:view/:year`} element={<ReviewPage />} />
              <Route path={RoutesConfig.review.pathname} element={<ReviewPage />} />
              <Route path={RoutesConfig.settings.pathname} element={<SettingsPage />} />
              <Route path={RoutesConfig.signIn.pathname} element={<SignInPage />} />
              <Route path={RoutesConfig.signOut.pathname} element={<SignOutPage />} />
              {!disableSignup && <Route path={RoutesConfig.signUp.pathname} element={<SignupPage />}/>}
              <Route path="*" element={<NotFoundPage />}/>
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </AppStartup>
  );
}

export default App;
